import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Card, Col, Row, Spinner, Table } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import swal from "sweetalert";
import SignedBillActions from "../Common/SignedBillActions";

const money = (value) =>
 new Intl.NumberFormat("en-IN", {
 style: "currency",
 currency: "INR",
 }).format(Number(value || 0));

const statusVariant = (status) => {
 if (status === "Paid") return "success";
 if (status === "Partially Paid") return "warning";
 if (status === "Rejected") return "danger";
 if (status === "Submitted") return "info";
 return "secondary";
};

const VendorWorkOrderBillingView = () => {
 const { id } = useParams();
 const navigate = useNavigate();
 const [bill, setBill] = useState(null);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const loadBill = async () => {
 try {
 setLoading(true);
 const res = await api.get(`/vendor-work-order-billings/${id}`);
 if (res.data.success) setBill(res.data.data);
 } catch (error) {
 swal("Error", "Failed to load vendor bill", "error");
 } finally {
 setLoading(false);
 }
 };

 loadBill();
 }, [id]);

 const remaining = Math.max(
 Number(bill?.totalAmount || 0) - Number(bill?.paidAmount || 0),
 0
 );

 if (loading) {
 return <div className="text-center py-5"><Spinner /></div>;
 }

 if (!bill) {
 return (
 <Fragment>
 <PageTitle activeMenu="Vendor Bill View" motherMenu="Commercial" />
 <Card className="shadow-sm border-0">
 <Card.Body className="text-center text-muted py-5">
 Vendor bill not found.
 </Card.Body>
 </Card>
 </Fragment>
 );
 }

 return (
 <Fragment>
 <PageTitle activeMenu="Vendor Bill View" motherMenu="Commercial" />
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">
 {bill.billNo}
 <Badge bg={statusVariant(bill.status)} className="ms-2">{bill.status}</Badge>
 </Card.Title>
 <Button variant="light" onClick={() => navigate(-1)}>
 <i className="fa fa-arrow-left me-2"></i>Back
 </Button>
 </Card.Header>

 <Card.Body>
 {/* Header Info */}
 <Row className="g-3 mb-4">
 <Col md={4}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Vendor Work Order</div>
 <div className="fw-bold">
 {bill.vendorWorkOrder?.woNumber || "-"}
 {bill.vendorWorkOrder?.title && (
 <span className="text-muted fw-normal ms-1">— {bill.vendorWorkOrder.title}</span>
 )}
 </div>
 </div>
 </Col>
 <Col md={4}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Vendor</div>
 <div className="fw-bold">{bill.vendor?.name || "-"}</div>
 </div>
 </Col>
 <Col md={4}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Created By</div>
 <div className="fw-bold">{bill.createdBy?.name || "-"}</div>
 </div>
 </Col>
 <Col md={3}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Invoice No</div>
 <div className="fw-bold">{bill.invoiceNo || "-"}</div>
 </div>
 </Col>
 <Col md={3}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Invoice Date</div>
 <div className="fw-bold">
 {bill.invoiceDate
 ? new Date(bill.invoiceDate).toLocaleDateString("en-IN")
 : "-"}
 </div>
 </div>
 </Col>
 <Col md={3}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Paid</div>
 <div className="fw-bold text-success">{money(bill.paidAmount)}</div>
 </div>
 </Col>
 <Col md={3}>
 <div className="border rounded p-3 h-100">
 <div className="text-muted small">Remaining</div>
 <div className="fw-bold text-danger">{money(remaining)}</div>
 </div>
 </Col>
 {bill.remarks && (
 <Col md={12}>
 <div className="border rounded p-3">
 <div className="text-muted small">Remarks</div>
 <div>{bill.remarks}</div>
 </div>
 </Col>
 )}
 </Row>

 {/* Attachment */}
 {bill.attachment?.fileUrl && (
 <div className="mb-4">
 <a
 href={getFileUrl(bill.attachment.fileUrl)}
 target="_blank"
 rel="noreferrer"
 className="btn btn-outline-primary btn-sm"
 >
 <i className="fa fa-paperclip me-2"></i>
 {bill.attachment.fileName || "View Uploaded Bill"}
 </a>
 </div>
 )}

 <div className="mb-4 border rounded p-3">
 <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
 <div>
 <div className="text-muted small">Signed Bill</div>
 <div className="fw-semibold">
 {bill.signedCopy?.uploadedAt
 ? new Date(bill.signedCopy.uploadedAt).toLocaleDateString("en-IN")
 : "Not uploaded"}
 </div>
 </div>
 <SignedBillActions
 id={bill._id}
 type="vendor-work-order-billings"
 signedCopy={bill.signedCopy}
 onUploadSuccess={async () => {
 const res = await api.get(`/vendor-work-order-billings/${id}`);
 if (res.data.success) setBill(res.data.data);
 }}
 />
 </div>
 </div>

 {/* Items Table */}
 <div className="table-responsive mb-4">
 <Table bordered hover className="align-middle">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Item</th>
 <th>Unit</th>
 <th className="text-end">Qty</th>
 <th className="text-end">Rate</th>
 <th className="text-end">Amount</th>
 </tr>
 </thead>
 <tbody>
 {(bill.items || []).map((item, idx) => (
 <tr key={idx}>
 <td>{idx + 1}</td>
 <td>
 <div className="fw-semibold">{item.itemName}</div>
 {item.description && (
 <div className="small text-muted">{item.description}</div>
 )}
 </td>
 <td>{item.unit || "-"}</td>
 <td className="text-end">{item.quantity}</td>
 <td className="text-end">{money(item.rate)}</td>
 <td className="text-end">{money(item.amount)}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>

 {/* Tax Summary */}
 <Row className="justify-content-end mb-4">
 <Col md={4}>
 <div className="border rounded p-3">
 <div className="d-flex justify-content-between mb-1">
 <span className="text-muted">Sub Total</span>
 <strong>{money(bill.subTotal)}</strong>
 </div>
 {bill.cgst > 0 && (
 <div className="d-flex justify-content-between mb-1">
 <span className="text-muted">CGST ({bill.cgst}%)</span>
 <strong>{money(bill.cgstAmount)}</strong>
 </div>
 )}
 {bill.sgst > 0 && (
 <div className="d-flex justify-content-between mb-1">
 <span className="text-muted">SGST ({bill.sgst}%)</span>
 <strong>{money(bill.sgstAmount)}</strong>
 </div>
 )}
 {bill.igst > 0 && (
 <div className="d-flex justify-content-between mb-1">
 <span className="text-muted">IGST ({bill.igst}%)</span>
 <strong>{money(bill.igstAmount)}</strong>
 </div>
 )}
 <hr className="my-2" />
 <div className="d-flex justify-content-between text-primary">
 <span className="fw-semibold">Total</span>
 <strong>{money(bill.totalAmount)}</strong>
 </div>
 </div>
 </Col>
 </Row>

 {/* Payment Details */}
 {(bill.paidAmount > 0 || bill.paymentReference) && (
 <div className="border rounded p-3">
 <h6 className="mb-3">Payment Details</h6>
 <Row className="g-2">
 <Col sm={4}>
 <div className="text-muted small">Paid Amount</div>
 <div className="fw-semibold text-success">{money(bill.paidAmount)}</div>
 </Col>
 {bill.paidDate && (
 <Col sm={4}>
 <div className="text-muted small">Paid Date</div>
 <div>{new Date(bill.paidDate).toLocaleDateString("en-IN")}</div>
 </Col>
 )}
 {bill.paymentReference && (
 <Col sm={4}>
 <div className="text-muted small">Payment Reference</div>
 <div>{bill.paymentReference}</div>
 </Col>
 )}
 {bill.paymentRemarks && (
 <Col sm={12}>
 <div className="text-muted small">Payment Remarks</div>
 <div>{bill.paymentRemarks}</div>
 </Col>
 )}
 {bill.paidBy?.name && (
 <Col sm={4}>
 <div className="text-muted small">Paid By</div>
 <div>{bill.paidBy.name}</div>
 </Col>
 )}
 </Row>
 </div>
 )}
 </Card.Body>
 </Card>
 </Fragment>
 );
};

export default VendorWorkOrderBillingView;
